import React, { useEffect, useState, useRef } from 'react';
import { PowerBIEmbed } from 'powerbi-client-react';
import { Embed, IReportEmbedConfiguration, models } from 'powerbi-client';
import './PowerBiReport.scss';
import { getReportTokens } from 'src/api/reports';
import { FmtpError } from '@lanterds/types';
import { PAGE_INFO } from 'src/util/constants';
import { useFmtpNavigate } from 'src/hooks/useFmtpNavigate';

type TokensResponse = {
    embedToken: string;
    reportId: string;
    embedUrl: string;
    isPaginated: boolean;
    expiration: string;
};

const INTERVAL_TIME = 55 * 60 * 1000; // 55 Minutes;

export default function PowerBiReport({ currentReport }: { currentReport: string | null }) {
    const fmtpNavigate = useFmtpNavigate();
    const [isLoadingReport, setIsLoadingReport] = useState<boolean>(false);
    const [errorGettingReport, setErrorGettingReport] = useState<boolean>(false);
    // Need to use useRefs because setInterval() func may not have access to current state values
    const reportInfo = useRef<TokensResponse | null>(null);
    const embedReport = useRef<Embed | null>(null);

    useEffect(() => {
        reportInfo.current = null;
        embedReport.current = null;
        updateReport();
    }, [currentReport]);

    useEffect(() => {
        setIsLoadingReport(true);
        updateReport();

        const intervalId = setInterval(() => {
            if (reportInfo.current) {
                if (reportInfo.current.isPaginated) {
                    fmtpNavigate(`${PAGE_INFO['reports'].href}?reportExpired=true`);
                } else {
                    updateReport();
                }
            }
        }, INTERVAL_TIME);

        return () => clearInterval(intervalId);
    }, []);

    const updateReport = () => {
        setErrorGettingReport(false);

        if (currentReport) {
            if (!reportInfo.current) {
                setIsLoadingReport(true);
            }

            getReportTokens(currentReport)
                .then(async (tokens: TokensResponse) => {
                    if (embedReport.current) {
                        await embedReport.current.setAccessToken(tokens.embedToken);
                    } else {
                        reportInfo.current = tokens;
                    }
                })
                .catch((error) => {
                    console.error(error);
                    setErrorGettingReport(true);
                    throw new FmtpError({ message: `Error getting report: ${error}`, errorCode: 'SHU012' });
                })
                .finally(() => {
                    setIsLoadingReport(false);
                });
        } else {
            reportInfo.current = null;
        }
    };

    const getEmbedConfig = (tokens: TokensResponse) => {
        const embedConfig: IReportEmbedConfiguration = {
            type: 'report', // Supported types: report, dashboard, tile, visual, qna, paginated report and create
            id: tokens.reportId,
            accessToken: tokens.embedToken,
            tokenType: models.TokenType.Embed, // Use models.TokenType.Aad for SaaS embed
            settings: {
                panes: {
                    filters: {
                        expanded: false,
                        visible: false,
                    },
                },
                background: models.BackgroundType.Default,
            },
            ...(tokens.embedUrl ? { embedUrl: tokens.embedUrl } : null),
        };

        return embedConfig;
    };

    return (
        <>
            {isLoadingReport && <h1>Loading Report...</h1>}
            {errorGettingReport && <h1>There was an error getting this report. If this persists please contact Support</h1>}
            {!isLoadingReport && reportInfo.current && getEmbedConfig(reportInfo.current) && (
                <PowerBIEmbed
                    embedConfig={getEmbedConfig(reportInfo.current)}
                    cssClassName="powerBi"
                    getEmbeddedComponent={(report) => {
                        embedReport.current = report;
                    }}
                />
            )}
        </>
    );
}
